import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { format } from 'date-fns';
import { db } from '../firebase';
import {
  DEFAULT_ENTITLEMENTS,
  normalizeLegacyPlanId,
  type BuiltInPlanId,
  type PlanEntitlements,
  type PlanId,
} from '../billing/types';
import { adminBillingService } from '../adminBilling';
import type { AccountDoc, AccountMemberDoc, AccountUsage } from './types';

const ACCOUNTS_COL = 'accounts';

function currentPeriod(): string {
  return format(new Date(), 'yyyy-MM');
}

function emptyUsage(): AccountUsage {
  return {
    period: currentPeriod(),
    aiDrafts: 0,
    documentsCreated: 0,
    storageBytesUsed: 0,
  };
}

export function mapAccountDoc(id: string, data: Record<string, unknown>): AccountDoc {
  const sub = (data.subscription as Record<string, unknown>) ?? {};
  const usage = (data.usage as Record<string, unknown>) ?? {};
  return {
    id,
    ownerId: String(data.ownerId ?? id),
    name: String(data.name ?? 'Account'),
    planId: String(data.planId ?? sub.plan ?? 'free') as PlanId,
    subscription: {
      plan: String(sub.plan ?? data.planId ?? 'free') as PlanId,
      status: (sub.status as AccountDoc['subscription']['status']) ?? 'active',
      billingCycle: (sub.billingCycle as AccountDoc['subscription']['billingCycle']) ?? 'monthly',
      currentPeriodStart: sub.currentPeriodStart as string | undefined,
      currentPeriodEnd: sub.currentPeriodEnd as string | undefined,
      stripeCustomerId: sub.stripeCustomerId as string | undefined,
      stripeSubscriptionId: sub.stripeSubscriptionId as string | undefined,
      source: (sub.source as AccountDoc['subscription']['source']) ?? 'migration',
      grantedAt: sub.grantedAt as string | undefined,
      grantedBy: sub.grantedBy as string | undefined,
    },
    seatCount: Number(data.seatCount ?? 1),
    includedSeats: Number(data.includedSeats ?? 1),
    additionalSeats: Number(data.additionalSeats ?? 0),
    entitlementOverrides: data.entitlementOverrides as AccountDoc['entitlementOverrides'],
    usage: {
      period: String(usage.period ?? currentPeriod()),
      aiDrafts: Number(usage.aiDrafts ?? 0),
      documentsCreated: Number(usage.documentsCreated ?? 0),
      storageBytesUsed: Number(usage.storageBytesUsed ?? 0),
    },
    workspaceIds: Array.isArray(data.workspaceIds) ? (data.workspaceIds as string[]) : [],
    grandfatherStorageMb: data.grandfatherStorageMb as number | undefined,
    storageEnforcementEnabled: data.storageEnforcementEnabled !== false,
    createdAt: String(data.createdAt ?? new Date().toISOString()),
    updatedAt: String(data.updatedAt ?? new Date().toISOString()),
  };
}

export function resolveAccountPlanId(account: AccountDoc | null | undefined): string {
  if (!account) return 'free';
  return normalizeLegacyPlanId(String(account.planId ?? account.subscription?.plan ?? 'free'));
}

export async function getAccountById(accountId: string): Promise<AccountDoc | null> {
  const snap = await getDoc(doc(db, ACCOUNTS_COL, accountId));
  if (!snap.exists()) return null;
  return mapAccountDoc(snap.id, snap.data());
}

export async function getAccountForUser(userId: string): Promise<AccountDoc | null> {
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return null;
  const accountId = userSnap.data().accountId as string | undefined;
  if (accountId) {
    const account = await getAccountById(accountId);
    if (account) return account;
  }
  // Fallback: account id === owner uid for solo users
  const ownerAccount = await getAccountById(userId);
  if (ownerAccount) return ownerAccount;
  return null;
}

export async function getAccountEntitlements(account: AccountDoc): Promise<PlanEntitlements> {
  const planId = resolveAccountPlanId(account);
  const catalog = await adminBillingService.getPlanCatalog();
  const plan = catalog.find((p) => p.id === planId);
  const base =
    (plan?.entitlements as PlanEntitlements | undefined) ??
    DEFAULT_ENTITLEMENTS[planId as BuiltInPlanId] ??
    DEFAULT_ENTITLEMENTS.free;
  return { ...base, ...(account.entitlementOverrides ?? {}) };
}

export async function ensureAccountForUser(
  userId: string,
  userData: Record<string, unknown>,
): Promise<AccountDoc> {
  const existing = await getAccountForUser(userId);
  if (existing) return existing;

  const planId = normalizeLegacyPlanId(
    String(userData.planTier ?? (userData.subscription as { plan?: string })?.plan ?? 'free'),
  ) as PlanId;
  const builtIn = DEFAULT_ENTITLEMENTS[planId as BuiltInPlanId];
  const now = new Date().toISOString();
  const accountId = userId;
  const account: AccountDoc = {
    id: accountId,
    ownerId: userId,
    name: String(
      (userData.profileData as { firmName?: string })?.firmName ??
        userData.displayName ??
        'My Practice',
    ),
    planId,
    subscription: {
      plan: planId,
      status: 'active',
      billingCycle: 'monthly',
      source: 'migration',
      grantedAt: now,
    },
    seatCount: 1,
    includedSeats: builtIn?.includedUsers ?? 1,
    additionalSeats: 0,
    usage: emptyUsage(),
    workspaceIds: [userId],
    storageEnforcementEnabled: true,
    createdAt: now,
    updatedAt: now,
  };

  await setDoc(doc(db, ACCOUNTS_COL, accountId), {
    ...account,
    members: {},
  });
  await setDoc(
    doc(db, ACCOUNTS_COL, accountId, 'members', userId),
    {
      userId,
      role: 'owner',
      status: 'active',
      joinedAt: now,
    } satisfies AccountMemberDoc,
  );
  await setDoc(
    doc(db, 'users', userId),
    { accountId, updatedAt: now },
    { merge: true },
  );
  return account;
}

export async function listAccountMembers(accountId: string): Promise<AccountMemberDoc[]> {
  const snap = await getDocs(collection(db, ACCOUNTS_COL, accountId, 'members'));
  return snap.docs
    .map((d) => d.data() as AccountMemberDoc)
    .filter((m) => m.status === 'active');
}

export async function countAccountWorkspaces(accountId: string): Promise<number> {
  const account = await getAccountById(accountId);
  return account?.workspaceIds?.length ?? 0;
}

export type MigrationResult = {
  processed: number;
  created: number;
  updated: number;
  errors: string[];
};

/** Admin/migration: create accounts for all users missing accountId */
export async function migrateUsersToAccounts(maxUsers = 500): Promise<MigrationResult> {
  const result: MigrationResult = { processed: 0, created: 0, updated: 0, errors: [] };
  const usersSnap = await getDocs(query(collection(db, 'users'), limit(maxUsers)));

  for (const userDoc of usersSnap.docs) {
    result.processed += 1;
    try {
      const data = userDoc.data();
      const uid = userDoc.id;
      if (data.accountId) {
        result.updated += 1;
        continue;
      }
      const planRaw = normalizeLegacyPlanId(
        String(data.planTier ?? (data.subscription as { plan?: string })?.plan ?? 'free'),
      );
      const planId = planRaw as PlanId;
      const builtIn = DEFAULT_ENTITLEMENTS[planId as BuiltInPlanId] ?? DEFAULT_ENTITLEMENTS.free;
      const now = new Date().toISOString();
      const wsId = (data.currentWorkspaceId as string) ?? uid;

      const accountId = uid;
      const batch = writeBatch(db);
      batch.set(doc(db, ACCOUNTS_COL, accountId), {
        ownerId: uid,
        name: String(
          (data.profileData as { firmName?: string })?.firmName ??
            data.displayName ??
            'My Practice',
        ),
        planId,
        subscription: {
          plan: planId,
          status: 'active',
          billingCycle: 'monthly',
          source: 'migration',
          grantedAt: now,
        },
        seatCount: 1,
        includedSeats: builtIn.includedUsers,
        additionalSeats: 0,
        usage: {
          period: currentPeriod(),
          aiDrafts: Number((data.usagePeriod as { aiDrafts?: number })?.aiDrafts ?? 0),
          documentsCreated: Number(
            (data.usagePeriod as { documentsCreated?: number })?.documentsCreated ?? 0,
          ),
          storageBytesUsed: 0,
        },
        workspaceIds: [wsId],
        storageEnforcementEnabled: true,
        createdAt: now,
        updatedAt: now,
      });
      batch.set(doc(db, ACCOUNTS_COL, accountId, 'members', uid), {
        userId: uid,
        role: 'owner',
        status: 'active',
        joinedAt: now,
      });
      batch.set(
        doc(db, 'users', uid),
        {
          accountId,
          planTier: planId,
          updatedAt: now,
        },
        { merge: true },
      );
      await batch.commit();
      result.created += 1;
    } catch (e) {
      result.errors.push(`${userDoc.id}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  return result;
}

export async function syncUserPlanFromAccount(userId: string, account: AccountDoc): Promise<void> {
  const planId = resolveAccountPlanId(account);
  await updateDoc(doc(db, 'users', userId), {
    planTier: planId,
    subscription: {
      plan: planId,
      status: account.subscription.status,
      source: account.subscription.source,
      currentPeriodEnd: account.subscription.currentPeriodEnd ?? null,
    },
    updatedAt: new Date().toISOString(),
  });
}

export async function findAccountByOwner(ownerId: string): Promise<AccountDoc | null> {
  const q = query(collection(db, ACCOUNTS_COL), where('ownerId', '==', ownerId), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return mapAccountDoc(d.id, d.data());
}

export async function linkWorkspaceToAccount(accountId: string, workspaceId: string): Promise<void> {
  const account = await getAccountById(accountId);
  if (!account) return;
  const ids = new Set(account.workspaceIds ?? []);
  if (ids.has(workspaceId)) return;
  ids.add(workspaceId);
  await updateDoc(doc(db, ACCOUNTS_COL, accountId), {
    workspaceIds: [...ids],
    updatedAt: new Date().toISOString(),
  });
}

export async function updateAdditionalSeats(accountId: string, additionalSeats: number): Promise<void> {
  const account = await getAccountById(accountId);
  if (!account) return;
  const included = account.includedSeats;
  await updateDoc(doc(db, ACCOUNTS_COL, accountId), {
    additionalSeats: Math.max(0, additionalSeats),
    seatCount: Math.max(included + Math.max(0, additionalSeats), account.seatCount),
    updatedAt: new Date().toISOString(),
  });
}

export const accountService = {
  getAccountById,
  getAccountForUser,
  getAccountEntitlements,
  ensureAccountForUser,
  listAccountMembers,
  countAccountWorkspaces,
  migrateUsersToAccounts,
  syncUserPlanFromAccount,
  findAccountByOwner,
  resolveAccountPlanId,
  mapAccountDoc,
  linkWorkspaceToAccount,
  updateAdditionalSeats,
};
